import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import {
    sensitiveInfoRevealConfirmOpenState,
    showSensitiveInfoState,
} from '~/state/atoms/viewerAtoms';

interface SensitiveInfoReveal {
    isInfoShown: boolean;
    isConfirmOpen: boolean;
    requestReveal: () => void;
    confirmReveal: () => void;
    cancelReveal: () => void;
    hideInfo: () => void;
    toggleWithGate: () => void;
}

/**
 * Owns the PHI-reveal confirmation policy. Showing protected patient
 * information is gated behind an explicit confirmation; hiding is instant
 * and ungated. showSensitiveInfoState is flipped to true in exactly one
 * place — confirmReveal — so PHI cannot render before the user confirms.
 */
export default function useSensitiveInfoReveal(): SensitiveInfoReveal {
    const [isInfoShown, setShowSensitiveInfo] = useRecoilState(
        showSensitiveInfoState
    );
    const [isConfirmOpen, setConfirmOpen] = useRecoilState(
        sensitiveInfoRevealConfirmOpenState
    );

    const requestReveal = useCallback((): void => {
        setConfirmOpen(true);
    }, [setConfirmOpen]);

    const confirmReveal = useCallback((): void => {
        setShowSensitiveInfo(true);
        setConfirmOpen(false);
    }, [setShowSensitiveInfo, setConfirmOpen]);

    const cancelReveal = useCallback((): void => {
        setConfirmOpen(false);
    }, [setConfirmOpen]);

    const hideInfo = useCallback((): void => {
        setShowSensitiveInfo(false);
    }, [setShowSensitiveInfo]);

    const toggleWithGate = useCallback((): void => {
        if (isInfoShown) {
            hideInfo();
            return;
        }
        requestReveal();
    }, [isInfoShown, hideInfo, requestReveal]);

    return {
        isInfoShown,
        isConfirmOpen,
        requestReveal,
        confirmReveal,
        cancelReveal,
        hideInfo,
        toggleWithGate,
    };
}
